/**
 * PointsDisplay Component
 * 
 * Shows the mysterious point counter in the header.
 * When clicked, shows a modal with intentionally vague explanation.
 * 
 * ALWAYS shows for logged-in users, even with 0 points.
 */

import React, { useState, useEffect } from 'react';
import '../styles/PointsDisplay.css';

export default function PointsDisplay() {
  // Initialize points to 0, not null - we always want to show something
  const [points, setPoints] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    fetchPoints();
  }, []);

  const fetchPoints = async () => {
    try {
      const response = await fetch('/points/me', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPoints(data.total_points ?? 0);
      }
      // On any error, just keep showing 0 - don't hide the component
    } catch (err) {
      console.log('Points fetch error:', err);
      // Keep showing 0
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await fetch('/points/me/history?limit=10', {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data.events || []);
      }
    } catch (err) {
      console.log('History not available');
    }
  };

  const openModal = () => {
    setShowModal(true);
    fetchHistory();
  };

  // ALWAYS render - never return null for logged-in users
  // The Navbar already gates this component behind auth check

  return (
    <>
      <button className="points-display" onClick={openModal} title="Your points">
        <span className="points-icon">✦</span>
        <span className="points-count">{points.toLocaleString()}</span>
      </button>

      {showModal && (
        <div className="points-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="points-modal" onClick={e => e.stopPropagation()}>
            <button className="points-modal-close" onClick={() => setShowModal(false)}>×</button>
            
            <div className="points-modal-content">
              <div className="points-total">
                <span className="points-total-icon">✦</span>
                <span className="points-total-number">{points.toLocaleString()}</span>
                <span className="points-total-label">points</span>
              </div>

              <p className="points-explanation">
                You're earning points for platform activity. We're tracking these for the future.
              </p>
              <p className="points-mystery">
                That's all we'll say for now.
              </p>

              {history.length > 0 ? (
                <div className="points-history">
                  <h4>Recent Activity</h4>
                  <ul>
                    {history.map(event => (
                      <li key={event.id} className="points-event">
                        <span className="points-event-action">
                          {formatAction(event.action)}
                        </span>
                        <span className="points-event-amount">
                          +{event.points}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="points-history">
                  <p className="points-empty">
                    Start using the platform to earn points!
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function formatAction(action) {
  const labels = {
    'account_creation': 'Joined',
    'email_verification': 'Verified email',
    'wallet_addition': 'Connected wallet',
    'daily_login': 'Daily visit',
    'job_creation': 'Created project',
    'subtask_completion': 'Completed task',
    'first_try_bonus': 'Quality bonus'
  };
  return labels[action] || action.replace(/_/g, ' ');
}
